import React from 'react';

export type PieSegment = { label: string; value: number; color: string };

type MiniPieChartProps = {
    segments: PieSegment[];
    size?: number;
};

// Extends each wedge's trailing edge slightly past its true angle so adjoining segments overlap by
// a sub-pixel amount — without it, two independently anti-aliased path edges leave a hairline seam
// (visible as a notch of the neighboring color) where they meet.
const SEAM_OVERLAP_RADIANS = 0.003;

/** A point on a circle of radius `r` centered at (cx, cy), `angle` radians clockwise from 12 o'clock. */
function pointOnCircle(cx: number, cy: number, r: number, angle: number) {
    return { x: cx + r * Math.sin(angle), y: cy - r * Math.cos(angle) };
}

/** SVG path `d` for one donut wedge (innerR..outerR) between two clockwise angles, in radians. */
function ringWedgePath(cx: number, cy: number, innerR: number, outerR: number, startAngle: number, endAngle: number): string {
    const outerStart = pointOnCircle(cx, cy, outerR, startAngle);
    const outerEnd = pointOnCircle(cx, cy, outerR, endAngle);
    const innerEnd = pointOnCircle(cx, cy, innerR, endAngle);
    const innerStart = pointOnCircle(cx, cy, innerR, startAngle);
    const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;

    return [
        `M ${outerStart.x} ${outerStart.y}`,
        `A ${outerR} ${outerR} 0 ${largeArc} 1 ${outerEnd.x} ${outerEnd.y}`,
        `L ${innerEnd.x} ${innerEnd.y}`,
        `A ${innerR} ${innerR} 0 ${largeArc} 0 ${innerStart.x} ${innerStart.y}`,
        'Z',
    ].join(' ');
}

/**
 * Small SVG donut chart. Each segment is an explicit ring-wedge `<path>` rather than a stacked
 * stroke-dasharray circle — the dasharray approach left a visible seam/notch where two segments'
 * colors met. Kept dependency-free since the project has no charting library installed.
 *
 * `size` only fixes the internal viewBox coordinate space (the slice proportions are relative, so
 * any value works) — the rendered size is fluid, filling whatever width its CSS container gives
 * it, so it scales automatically as the panel is resized.
 */
export default function MiniPieChart({ segments, size = 100 }: MiniPieChartProps) {
    const total = segments.reduce((sum, segment) => sum + segment.value, 0);
    const strokeWidth = size * 0.28;
    const outerRadius = size / 2;
    const innerRadius = outerRadius - strokeWidth;
    const center = size / 2;

    if (total === 0) {
        return (
            <svg viewBox={`0 0 ${size} ${size}`} className='mini-pie-chart mini-pie-chart--empty'>
                <circle cx={center} cy={center} r={outerRadius - strokeWidth / 2} fill='none' stroke='#b8bfc6' strokeWidth={strokeWidth} />
            </svg>
        );
    }

    const visibleSegments = segments.filter((segment) => segment.value > 0);
    // A single segment covering the whole circle is a degenerate arc (identical start/end point),
    // so it's drawn as two half-circle wedges instead.
    const isFullCircle = visibleSegments.length === 1;

    let angle = 0;
    return (
        <svg viewBox={`0 0 ${size} ${size}`} className='mini-pie-chart'>
            {visibleSegments.map((segment, index) => {
                const sweep = (segment.value / total) * 2 * Math.PI;
                const start = angle;
                const end = angle + sweep + SEAM_OVERLAP_RADIANS;
                angle = start + sweep;

                const path = isFullCircle
                    ? `${ringWedgePath(center, center, innerRadius, outerRadius, start, start + Math.PI)} ${ringWedgePath(center, center, innerRadius, outerRadius, start + Math.PI, start + 2 * Math.PI + SEAM_OVERLAP_RADIANS)}`
                    : ringWedgePath(center, center, innerRadius, outerRadius, start, end);

                return (
                    <path
                        key={`${segment.label}-${index}`}
                        d={path}
                        fill={segment.color}
                        data-tooltip={`${segment.label}: ${segment.value}`}
                    />
                );
            })}
        </svg>
    );
}
